import React, { useEffect, useState } from "react";
import profileConfigService from "../../../services/profileConfigService";
import {
  CASH_BASED_DEPLOYMENT_KEY,
  type CashDeploymentMode,
  cashDeploymentModeToSettingValue,
} from "../../../utils/cashDeploymentSettings";
import EnhancedTooltip from "../../../components/EnhancedTooltip";

interface CashDeploymentSettingPanelProps {
  mode: CashDeploymentMode | null;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
  onShowExplainer: () => void;
  appearance?: "default" | "alpha";
}

const CashDeploymentSettingPanel: React.FC<CashDeploymentSettingPanelProps> = ({
  mode,
  onSaved,
  onCancel,
  onShowExplainer,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const [draft, setDraft] = useState<CashDeploymentMode>(mode ?? "auto-deploy");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(mode ?? "auto-deploy");
    setError(null);
  }, [mode]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      await profileConfigService.patchUserSettings({
        [CASH_BASED_DEPLOYMENT_KEY]: cashDeploymentModeToSettingValue(draft),
      });
      await onSaved();
    } catch {
      setError("Failed to update cash deployment setting.");
    } finally {
      setLoading(false);
    }
  };

  const optionLabelClass = isAlpha
    ? "flex cursor-pointer items-start gap-3 rounded-v7-control border border-v7-ink/20 bg-v7-white p-4 transition-colors hover:bg-v7-paper"
    : "flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50 dark:border-rp-separator dark:bg-black dark:hover:bg-rp-surface";
  const radioClass = isAlpha
    ? "mt-1 h-4 w-4 border-v7-ink/30 text-v7-ink focus:ring-v7-bronze-dark"
    : "mt-1 h-4 w-4 border-gray-300 text-rp-amber focus:ring-rp-amber dark:border-rp-separator";
  const optionTitleClass = isAlpha
    ? "block text-sm font-semibold text-v7-ink"
    : "block text-sm font-semibold text-gray-900 dark:text-white";
  const optionDescClass = isAlpha
    ? "mt-1 block text-sm text-v7-ink-soft"
    : "mt-1 block text-sm text-gray-600 dark:text-gray-300";

  return (
    <div className="space-y-5" data-appearance={appearance}>
      <div className="flex items-start justify-between gap-3">
        <p className={`text-sm ${isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}`}>
          When new cash arrives in your account, choose whether it is deployed into your strategy or held in reserve.
        </p>
        <EnhancedTooltip
          triggerVariant="callout"
          triggerLabel="Learn"
          title="How cash deployment works"
          content="Open the animated walkthrough to compare auto-deploy and reserve cash."
          clickHint="Click to open animated example"
          onClick={onShowExplainer}
          maxWidthClass="max-w-sm"
          appearance={appearance}
        />
      </div>

      <div className="space-y-3">
        <label className={optionLabelClass}>
          <input
            type="radio"
            name="cash-deployment-mode"
            value="auto-deploy"
            checked={draft === "auto-deploy"}
            onChange={() => setDraft("auto-deploy")}
            className={radioClass}
          />
          <span>
            <span className={optionTitleClass}>Auto-deploy</span>
            <span className={optionDescClass}>
              New cash is deployed to maintain your current strategy ratios.
            </span>
          </span>
        </label>

        <label className={optionLabelClass}>
          <input
            type="radio"
            name="cash-deployment-mode"
            value="reserve"
            checked={draft === "reserve"}
            onChange={() => setDraft("reserve")}
            className={radioClass}
          />
          <span>
            <span className={optionTitleClass}>Reserve cash</span>
            <span className={optionDescClass}>
              New cash is held undeployed and your allocation percentages adjust to reflect it.
            </span>
          </span>
        </label>
      </div>

      {error && (
        <div className={isAlpha
          ? "rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-3 text-sm text-v7-rust"
          : "rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"}>
          {error}
        </div>
      )}

      <div className={`flex justify-end gap-3 border-t pt-4 ${isAlpha ? "border-v7-ink/10" : "border-gray-200 dark:border-rp-separator"}`}>
        <button
          type="button"
          onClick={onCancel}
          className={isAlpha
            ? "rounded-v7-control border border-v7-ink/25 bg-v7-white px-4 py-2 text-sm font-semibold text-v7-ink-soft transition-colors hover:border-v7-ink"
            : "rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={loading}
          className={isAlpha
            ? "rounded-v7-control bg-v7-ink px-4 py-2 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft disabled:cursor-not-allowed disabled:bg-v7-mist disabled:text-v7-ink/60"
            : "rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-60"}
        >
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default CashDeploymentSettingPanel;
